import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Chip,
  Stack,
  Radio,
  Grid,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Checkbox,
  Tooltip,
} from "@mui/material";
import {
  Close,
  Search,
  ViewList,
  ViewModule,
} from "@mui/icons-material";
import { api } from "../../../../api/api";
import {
  IWhatsAppTemplate,
  IWhatsAppTemplateData,
} from "../../../../redux/whatsappTemplate/whatsappTemplate.types";
import { PreviewTemplate } from "../../../../components/Preview/PreviewTemplate";
import WhatsappBgImg from "../../../../assets/whatsapp_bg_img.jpg";
import { formatText } from "../../../../helpers";
import { LoadState } from "../../../../constants/enums";

// ============================================
// NORMALIZE HELPER
// ============================================

export const normalizeTemplate = (template: IWhatsAppTemplate): IWhatsAppTemplateData => {
  const components = template.components || [];
  return {
    ...template,
    name: template.name || "",
    category: (template as any).category || "",
    example: { header_text: [], body_text: [] },
    company_outlet_uuid: template.company_outlet_uuid || "",
    meta_id: (template as any).meta_id || null,
    template_id: template.template_id || undefined,
    header: (components.find((c: any) => c.type === "HEADER") as any) || {
      type: "HEADER",
      text: "",
      format: null,
      example: { header_text: [[]], header_handle: null },
    },
    body: (components.find((c: any) => c.type === "BODY") as any) || {
      type: "BODY",
      text: "",
      example: { body_text: [[]] },
    },
    footer: (components.find((c: any) => c.type === "FOOTER") as any) || {
      type: "FOOTER",
      text: "",
    },
    buttons: (components.find((c: any) => c.type === "BUTTONS") as any) || {
      type: "BUTTONS",
      buttons: [],
    },
  } as IWhatsAppTemplateData;
};

// ============================================
// REUSABLE TEMPLATE CARD COMPONENT
// ============================================

interface WhatsAppTemplateCardProps {
  template: IWhatsAppTemplateData;
  isSelected: boolean;
  onSelect: () => void;
  showCheckbox?: boolean;
  disabled?: boolean;
}

/**
 * Reusable WhatsApp template card with:
 * - WhatsApp tiled background
 * - Template name in top-left
 * - Checkbox in top-right
 * - PreviewTemplate rendered inside
 */
export const WhatsAppTemplateCard: React.FC<WhatsAppTemplateCardProps> = ({
  template,
  isSelected,
  onSelect,
  showCheckbox = true,
  disabled = false,
}) => {
  return (
    <Card
      onClick={() => !disabled && onSelect()}
      sx={{
        height: "100%",
        minHeight: 380,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        borderRadius: 2,
        boxShadow: isSelected ? 0 : 1,
        border: isSelected ? "2px solid" : "none",
        borderColor: isSelected ? "primary.main" : "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        backgroundColor: "#efeae2",
        backgroundImage: `url(${WhatsappBgImg})`,
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        backgroundBlendMode: "multiply",
        transition: "all 0.2s",
        "&:hover": {
          boxShadow: disabled ? 1 : 4,
        },
        margin: 0
      }}
    >
      {/* Checkbox — top right */}
      {showCheckbox && (
        <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
          <Checkbox
            checked={isSelected}
            onChange={() => onSelect()}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
            color="primary"
          />
        </Box>
      )}

      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Template Name — top left */}
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            mb: 1,
            pr: 4,
          }}
        >
          {formatText(template.name)}
        </Typography>

        {/* Preview Template */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <PreviewTemplate
            template={template}
            loading={LoadState.InProgress}
            headerType={template.header?.format || ""}
            bgImage={false}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================
// SKELETON CARDS
// ============================================

export const WhatsAppTemplateCardSkeleton: React.FC<{ count?: number }> = ({
  count = 3,
}) => (
  <Grid container spacing={2}>
    {Array.from({ length: count }).map((_, idx) => (
      <Grid item xs={12} sm={6} md={4} key={idx}>
        <Card
          sx={{
            minHeight: 380,
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            boxShadow: 1,
            backgroundColor: "#efeae2",
            backgroundImage: `url(${WhatsappBgImg})`,
            backgroundRepeat: "repeat",
            backgroundSize: "auto",
            backgroundBlendMode: "multiply",
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="70%" height={28} sx={{ mb: 1 }} />
            <Box sx={{ display: "flex", alignItems: "flex-end", height: "100%" }}>
              <Box sx={{ width: "100%" }}>
                <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2 }} />
                <Skeleton variant="text" width="85%" height={20} sx={{ mt: 1 }} />
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// ============================================
// PICKER COMPONENT
// ============================================

export interface WhatsAppTemplatePickerProps {
  companyOutletUuid: string;
  selectedTemplateUuid?: string;
  onTemplateSelect: (template: IWhatsAppTemplateData) => void;
}

/**
 * Reusable WhatsApp template picker with card and table views.
 * No Redux or MarketCampaignContext dependency.
 */
export const WhatsAppTemplatePicker: React.FC<WhatsAppTemplatePickerProps> = ({
  companyOutletUuid,
  selectedTemplateUuid,
  onTemplateSelect,
}) => {
  const [templates, setTemplates] = useState<IWhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState("");
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [viewMode, setViewMode] = useState<"table" | "cards">("cards");

  const pageSize = 9;

  const fetchTemplates = async (pageNo: number, search?: string) => {
    setLoading(true);
    try {
      let url = `/template/get-whatsapp-template?pageNo=${pageNo}&pageLimit=${pageSize}&itemPerPage=${pageSize}&status=APPROVED`;
      if (companyOutletUuid) {
        url += `&company_outlet_uuid=${companyOutletUuid}`;
      }
      if (search) {
        url += `&columns=${JSON.stringify(["name"])}&value=${search}`;
      }
      const res = await api.get(url, {
        params: {
          template_for: "PAYMENT_REMINDER"
        }
      });
      setTemplates(res.data.data || []);
      setTotalRecords(res.data.totalRecords || 0);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyOutletUuid) {
      fetchTemplates(page, searchName);
    }
  }, [companyOutletUuid, page]);

  // Debounced search
  const searchTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchName(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPage(1);
      fetchTemplates(1, value);
    }, 400);
  };

  const handleSelect = (template: IWhatsAppTemplate) => {
    const normalized = normalizeTemplate(template);
    onTemplateSelect(normalized);
  };

  return (
    <Box>
      {/* Search + View Toggle */}
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search templates by name..."
          value={searchName}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchName && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchName("");
                    setPage(1);
                    fetchTemplates(1, "");
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, val) => val && setViewMode(val)}
        >
          <ToggleButton value="cards">
            <ViewModule fontSize="small" />
          </ToggleButton>
          <ToggleButton value="table">
            <ViewList fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Loading */}
      {loading ? (
        <WhatsAppTemplateCardSkeleton count={3} />
      ) : templates.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No approved templates found
          </Typography>
        </Box>
      ) : (
        <>
          {/* Card View */}
          {viewMode === "cards" && (
            <Grid container spacing={1}>
              {templates.map((template) => {
                const isSelected = selectedTemplateUuid === (template as any).whatsapp_template_uuid || selectedTemplateUuid === template.name;
                const normalized = normalizeTemplate(template);
                return (
                  <Grid item xs={12} sm={6} md={4} key={template.name}>
                    <WhatsAppTemplateCard
                      template={normalized}
                      isSelected={isSelected}
                      onSelect={() => handleSelect(template)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Table View */}
          {viewMode === "table" && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Template Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.map((template) => {
                    const isSelected = selectedTemplateUuid === (template as any).whatsapp_template_uuid || selectedTemplateUuid === template.name;
                    return (
                      <TableRow
                        key={template.name}
                        hover
                        onClick={() => handleSelect(template)}
                        sx={{
                          cursor: "pointer",
                          bgcolor: isSelected ? "action.selected" : "inherit",
                        }}
                      >
                        <TableCell>
                          <Radio size="small" checked={isSelected} onChange={() => handleSelect(template)} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{template.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={template.category || "—"} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={template.status || "—"}
                            size="small"
                            color={template.status === "APPROVED" ? "success" : "default"}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {totalRecords > pageSize && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1.5}>
              <Typography variant="caption" color="text.secondary">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalRecords)} of {totalRecords}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip label="Previous" size="small" clickable disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                <Chip label="Next" size="small" clickable disabled={page * pageSize >= totalRecords} onClick={() => setPage((p) => p + 1)} />
              </Stack>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};
